import type { Account } from '../account/account';
import { CellularSettings } from '../apn/cellular-settings';
import { Taggable } from '../core/core';
import { GasSetting } from '../gas/gas';
import { HazardLevel } from '../safety-observations/safety-observation';
import { i18nLocale } from '../translate/translate';
import { User } from '../user/User';
import { WranglerResponse } from '../wranglers/wrangler';


export interface Beacon {
    id: string;
    name: string;
}

export interface FacilityPerson {
    personId: string;
    facilityId: string;
    group: string;
}

export interface AddFacilityPersonRequest {
    personId: string;
    facilityId: string;
    group: string;
}

export enum ShareFacilityInstanceState {
    Pending = 'pending',
    Active = 'active',
    Rejected = 'rejected',
}

export interface ShareFacilityInstance {
    id?: string;
    /** The user that initiated the share.*/
    hostUserId: string;
    /** The account that the host facility belongs to. */
    hostAccountId: string;
    /** The facility being shared. */
    hostFacilityId: string;
    /** The facility who's users will be synced to the host account */
    sharedFacilityId?: string;
    /** users in the shared account that have access to accept/reject the request */
    sharedUserIds: string[];
    /** The accountId that is being invited to sync users to the hostFacility. */
    sharedAccountId: string;
    /** The current state of the share facility instance. */
    state: ShareFacilityInstanceState;
    /** The default craft on the host account shared users will be created with */
    defaultCraftId: string;
    /** The default permission group on the host account's facility shared users will be created with */
    defaultPermissionGroupId: string;
}

export interface ShareFacilityInstanceResponse extends ShareFacilityInstance {
    hostUser: User;
    sharedUsers: User[];
    hostAccount?: Account;
    hostFacility?: Facility;
    sharedFacility?: Facility;
    sharedAccount?: Account;
    defaultCraftName: string;
    defaultPermissionGroupName: string;
}

export interface Facility extends Omit<Taggable, 'facilityId'> {
    id?: string;
    name: string;
    address: string;
    location: number[];
    bounds: number[][];
    zoom: number;
    image?: {
        uri?: string;
        placement?: {
            bounds: number[][];
            rotation: number;
            vertices: number[][];
        };
    };
    timezone?: string;
    people?: FacilityPerson[];
    settings?: FacilitySettings;
    waltSettings?: WaltSettings;
    trackingCircles?: Array<{
        latitude: number;
        longitude: number;
        radius: number;
    }>;
    /** Ids of the modules assigned to this site. Absent or empty means the site uses the account default, else Core. */
    modules?: string[];
    updated?: Date;
}

export interface FacilityResponse extends Facility {
    id: string; // Re-declare to not be optional.

    accountId?: string;
    beacons?: Beacon[];
    wranglers: WranglerResponse[];
}

export interface FacilitySettings {
    gases: GasSetting[];
    locationTimeoutHrs?: number;
    safetyObservation?: {
        categories?: Array<{
            name: string;
            hazardLevel: HazardLevel;
            readOnly: boolean;
        }>;
        resolutionTypes?: string[];
    };
    peer?: {
        enterDistance?: number[];
        enterTime?: number;
        exitDistance?: number[];
        exitTime?: number;
    };
    beacon?: {
        enterDistance?: number[];
        enterTime?: number;
        exitDistance?: number[];
        nearestBuffer?: number;
        exitTime?: number;
    };
    leaving?: {
        enabled?: boolean;
        title?: string;
        body?: string;
    };
    music?: {
        enabled?: boolean;
        headphonesRequired?: boolean;
        maxVolume?: number;
        bannedGeofenceIds?: string[];
    };
    walt?: {
        minWifiRssi?: number;
    };
    excludeCompanies?: string[];
}

export interface FacilityControlCenterResponse extends Facility {
    createdBy?: string; // used to send to CC when created.
    modifiedBy?: string; // used to send to CC when updated or deleted.
    accountId?: string; // used to send to CC when created.
}

export type UpdateSettingsRequest = Partial<FacilitySettings>;

export interface Simulator {
    personCount?: number;
    itemCount?: number;
    running?: boolean;
    error?: string;
    enabled?: Date;
    disabled?: Date;
}

export interface WeavixFacilityRequest {
    name: string;
    address: string;
    location: number[];
    bounds?: number[][];
    locale: i18nLocale;
    overrideWaltOta?: string;
    overrideWaltOta5g?: string;
    overrideWaltApk?: string;
    useDualSims?: boolean;
    waltAccessories?: WaltAccessoryType[];
}

export interface FacilityChangesResponse {
    added: FacilityControlCenterResponse[];
    updated: FacilityControlCenterResponse[];
    deleted: FacilityControlCenterResponse[];
    lastDate: Date;
    lastId: string;
}

export const DEFAULT_PTT_JITTER_DELAY_MS = 500;
export const OLD_DEFAULT_PTT_JITTER_DELAY_MS = 167;

/**
 * Settings for the network switcher that automatically switches between SIMs based on signal quality.
 */
export interface NetworkSwitchSettings {
    /**
     * How much delay to add to the first PTT packet to account for network jitter.
     * Default: 167 ms
     */
    pttJitterDelayMs: number;
    /**
     * How often we tell android to try to refresh and check cell signals (not that android likes to update as often as we call).
     * Default: 10_000 ms
     */
    checkEveryMs: number;
    /**
     * If we just switched SIMs, how long before we will try switching back.
     * Default: 15_000 ms
     */
    switchBackDelayMs: number;
    /**
     * How long after a PTT is pressed that we will not try to failover (adds banner time, set negative to counter banner time).
     * Default: 30_000 ms
     */
    failoverBadSignalDelayMs: number;
    /**
     * If the primary SIM has bad signal, how long we will wait for it to try to get good signal for failing over (only applies to docking and perimeter triggers).
     * Default: 30_000 ms
     */
    recentPttDelayMs: number;
    /**
     * How many times a SIM must fail to connect with better signal before it is considered bad and will not be switched back to except manually.
     * Default: 1
     */
    simFailureThreshold: number;
    /**
     * How long after a SIM is deemed disconnected or with poor signal that it will actually switch if it is still in that state.
     * Default: 7_500 ms
     */
    disconnectedDelayMs: number;
    /**
     * Delay due to signal quality changes before we will consider switching to another SIM.
     * Default: 15_000 ms
     */
    signalDelayMs: number;
    /**
     * RSRP delta value above which we will consider the other signal good to switch to.
     * Default: 10
     */
    deltaRsrp: number;
    /**
     * RSRQ delta value above which we will consider the other signal good to switch to.
     * Default: 3
     */
    deltaRsrq: number;
    /**
     * Settings specific to SIM 1
     */
    sim0: {
        /**
         * RSRP value below which we will consider the signal bad.
         * Default: -125
         */
        exitRsrp: number;
        /**
         * RSRQ value below which we will consider the signal bad.
         * Default: -17
         */
        exitRsrq: number;
        /**
         * RSRP value above which we will consider this signal good to switch back to.
         * Default: 0
         */
        entryRsrp: number;
        /**
         * RSRQ value above which we will consider this signal good to switch back to.
         * Default: 0
         */
        entryRsrq: number;
    };
    /**
     * Settings specific to SIM 2
     */
    sim1: {
        /**
         * RSSI value below which we will consider the signal bad.
         * Default: -125
         */
        exitRsrp: number;
        /**
         * RSRQ value below which we will consider the signal bad.
         * Default: -17
         */
        exitRsrq: number;
        /**
         * RSSI value above which we will consider this signal good to switch back to.
         * Default: 0
         */
        entryRsrp: number;
        /**
         * RSRQ value above which we will consider this signal good to switch back to.
         * Default: 0
         */
        entryRsrq: number;
    };
}

/**
 * Site-level settings that apply to all walts at the site.
 */
export interface WaltSettings {
    /** Number of seconds of no movement until walt requires PIN to unlock  */
    stationaryTime: number;

    /** If true, users must log in within undockAlarmTime seconds or else the walt will go into alarm */
    undockAlarmEnabled: boolean;

    /** After undocking, if a user has not logged in within this many seconds, then the walt sounds a continuous alarm (if undockAlarmEnabled is true) */
    undockAlarmTime: number;

    /** Info about how to automatically connect to the wifi on this site */
    wifiCredentials: WifiCredential[];

    /** List of not known WiFi networks that we can use for fingerprinting location */
    fingerprintingNetworks?: FingerprintingNetwork[];

    /** APN settings for Private Cellular SIMs */
    cellularSettings?: CellularSettings[];

    /** The time of day (seconds past 00:00:00) walts should automatically update if a new mandatory version is available */
    startUpdateTime?: number;

    /**
     * A url-encoded connection string used to generate a QR code.
     * A walt can scan this QR to retrieve wifi/cellular connection information if it cannot connect to the internet.
     * If it's already connected to the internet, then it will automatically retrieve new wifi/cellular connection information
     * from the wifiCredentials/cellularSettings fields.
     */
    connectCredentialsQr?: string;

    /**
     * array of accesssories on the facility
     * Only these options will be "detected" by walts assigned to this facility
     * @example ['VX400', 'speaker-mic', 'adapter']
     */
    accessories?: WaltAccessoryType[];

    /*
        Settings for network SIM switcher
    */
    networkSwitchSettings?: NetworkSwitchSettings;

    // Flag to override the mandatory walt version for the site to something newer
    overrideWaltOta?: string;
    overrideWaltOta5g?: string;
    overrideWaltApk?: string;
    useDualSims?: boolean;
}

export enum WifiAuthenticationType {
    Protected = 'protected',
    Unprotected = 'unprotected',
}

export interface WifiCredential {
    type: WifiAuthenticationType;
    ssid: string;
    password: string;
}

export interface FingerprintingNetwork {
    id: string;
    name: string;
    ssid: string;
}

export enum WaltAccessoryType {
    SpeakerMic = 'speaker-mic',
    Adapter = 'adapter',
    /**
     * Old one ear headset
     */
    VX400 = 'VX400',
    /**
     * New, louder one ear headset
     */
    VX400B = 'VX400-B',
}
